import {
  VIBE_CODING_GENERATE_VIBE_RESPONSE_BEGIN,
  VIBE_CODING_GENERATE_VIBE_RESPONSE_SUCCESS,
  VIBE_CODING_GENERATE_VIBE_RESPONSE_FAILURE,
  VIBE_CODING_GENERATE_VIBE_RESPONSE_DISMISS_ERROR,
  VIBE_CODING_GENERATE_VIBE_RESPONSE_EVENT,
  VIBE_CODING_STOP_GENERATION,
  VIBE_CODING_PLAN_SUCCESS,
} from './constants';
import {
  createInitialMilestones,
  updateMilestones,
} from '../../../features-v2/vibe-coding/utils/progressUtils';
import { normalizePlanProposal } from '../../../features-v2/vibe-coding/utils/planUtils';
import {
  generateVibeResponse as generateVibeResponseApi,
  startVibeGenerationStream,
} from '../services/vibeCodingService';
import {
  createRequestId as createRequestIdUtil,
  matchesRequest,
} from '../../../features-v2/vibe-coding/utils/requestUtils';

import {
  parseSseBuffer,
  readHttpErrorMessage,
} from '../../../features-v2/vibe-coding/utils/streamUtils';
import { mergeUniqueMessages } from '../../../features-v2/vibe-coding/utils/messageUtils';

let activeStreamAbortController = null;

const RUN_SCOPED_RESET = {
  budget: null,
  currentPhase: null,
  verificationResult: null,
  runMetadata: null,
  streamingMessage: '',
  activeArtifact: null,
  toolCalls: [],
};

const createRequestId = () => createRequestIdUtil('vibe-stream');

const matchesActiveRequest = (state, meta = {}) =>
  matchesRequest(state.activeStreamRequestId, meta.requestId);

export function stopVibeGeneration(options = {}) {
  return (dispatch) => {
    const silent = Boolean(options.silent);
    if (activeStreamAbortController) {
      activeStreamAbortController.abort({
        reason: silent ? 'navigation' : 'user_cancelled',
        silent,
      });
      activeStreamAbortController = null;
    }
    if (!silent) {
      dispatch({
        type: VIBE_CODING_STOP_GENERATION,
        data: { reason: 'user_cancelled', resumable: true },
        meta: options.meta || {},
      });
    }
  };
}

export function generateVibeResponse(args = {}) {
  return async (dispatch) => {
    const requestId = createRequestId();
    dispatch({
      type: VIBE_CODING_GENERATE_VIBE_RESPONSE_BEGIN,
      data: args.data,
      meta: { requestId },
    });
    try {
      const data = await generateVibeResponseApi(args);
      dispatch({
        type: VIBE_CODING_GENERATE_VIBE_RESPONSE_SUCCESS,
        data,
        meta: { requestId },
      });
      return { ok: true, data };
    } catch (error) {
      dispatch({
        type: VIBE_CODING_GENERATE_VIBE_RESPONSE_FAILURE,
        data: { error },
        meta: { requestId },
      });
      return { ok: false, error };
    }
  };
}

export function generateVibeResponseStream(args = {}) {
  return async (dispatch) => {
    if (activeStreamAbortController) {
      activeStreamAbortController.abort({ reason: 'superseded', silent: true });
    }
    activeStreamAbortController = new AbortController();
    const signal = activeStreamAbortController.signal;
    const requestId = createRequestId();
    const contextId = `${args.projectId || ''}:${(args.data && args.data.conversationId) || ''}`;

    dispatch({
      type: VIBE_CODING_GENERATE_VIBE_RESPONSE_BEGIN,
      data: args.data,
      meta: { requestId, contextId },
    });

    try {
      const response = await startVibeGenerationStream(args, signal);
      if (!response.ok || !response.body) {
        const message = await readHttpErrorMessage(response);
        const error = new Error(message);
        error.code = 'VIBE_STREAM_HTTP_ERROR';
        error.retryable = response.status >= 500;
        dispatch({
          type: VIBE_CODING_GENERATE_VIBE_RESPONSE_FAILURE,
          data: { error },
          meta: { requestId },
        });
        return { ok: false, error, code: error.code, retryable: error.retryable };
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completed;
      let failed;
      let stopped;
      let planReady;
      let malformedCount = 0;
      let streamTerminal = false;

      const consumeEvents = (events) => {
        events.forEach((event) => {
          dispatch({
            type: VIBE_CODING_GENERATE_VIBE_RESPONSE_EVENT,
            data: event,
            meta: { requestId },
          });
          if (event.type === 'plan_ready') {
            planReady = event;
            streamTerminal = true;
          }
          if (event.type === 'completed' && event.message) {
            completed = event;
            streamTerminal = true;
          }
          if (event.type === 'stopped') {
            stopped = event;
            streamTerminal = true;
          }
          if (event.type === 'failed') {
            failed = event;
            streamTerminal = true;
            const errorMessage =
              typeof event.error === 'string' ? event.error : 'Unable to generate a response.';
            const streamError = new Error(errorMessage);
            if (event.code) streamError.code = event.code;
            streamError.retryable = Boolean(event.retryable);
            dispatch({
              type: VIBE_CODING_GENERATE_VIBE_RESPONSE_FAILURE,
              data: { error: streamError },
              meta: { requestId },
            });
          }
        });
      };

      while (true) {
        if (streamTerminal) {
          break;
        }
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const parsed = parseSseBuffer(buffer);
        buffer = parsed.remainder;
        malformedCount += parsed.malformed;
        consumeEvents(parsed.events);
      }
      buffer += decoder.decode();
      if (buffer.trim()) {
        const parsed = parseSseBuffer(`${buffer}\n\n`);
        malformedCount += parsed.malformed;
        consumeEvents(parsed.events);
      }

      if (planReady) {
        const proposal = planReady.proposal || planReady;
        dispatch({
          type: VIBE_CODING_PLAN_SUCCESS,
          data: {
            ...proposal,
            conversationId: planReady.conversationId || proposal.conversationId,
          },
          meta: { requestId },
        });
        return {
          ok: true,
          planReady: true,
          data: {
            ...proposal,
            conversationId: planReady.conversationId || proposal.conversationId,
          },
        };
      }

      if (stopped) {
        return { ok: true, stopped: true, data: stopped };
      }
      if (failed) {
        const errorMessage =
          typeof failed.error === 'string' ? failed.error : 'Unable to generate a response.';
        const error = new Error(errorMessage);
        error.code = failed.code;
        error.retryable = Boolean(failed.retryable);
        dispatch({
          type: VIBE_CODING_GENERATE_VIBE_RESPONSE_FAILURE,
          data: { error },
          meta: { requestId },
        });
        return {
          ok: false,
          error,
          code: failed.code,
          retryable: Boolean(failed.retryable),
        };
      }
      if (!completed) {
        dispatch({
          type: VIBE_CODING_STOP_GENERATION,
          data: {
            reason: 'connection_lost',
            resumable: true,
          },
          meta: { requestId },
        });
        return {
          ok: true,
          stopped: true,
          data: {
            reason: 'connection_lost',
            resumable: true,
          },
        };
      }
      dispatch({
        type: VIBE_CODING_GENERATE_VIBE_RESPONSE_SUCCESS,
        data: completed,
        meta: { requestId },
      });
      return { ok: true, data: completed };
    } catch (error) {
      const abortReason = signal.reason;
      const silent =
        Boolean(abortReason && abortReason.silent) ||
        (abortReason && abortReason.reason === 'navigation') ||
        (abortReason && abortReason.reason === 'superseded');
      if (signal.aborted || (error && error.name === 'AbortError')) {
        dispatch({
          type: VIBE_CODING_STOP_GENERATION,
          data: { reason: silent ? 'navigation' : 'user_cancelled', resumable: !silent },
          meta: { requestId, silent },
        });
        return {
          ok: true,
          stopped: true,
          data: {
            reason: silent ? 'navigation' : 'user_cancelled',
            resumable: !silent,
          },
        };
      }
      const isNetworkError =
        error &&
        (error.name === 'TypeError' ||
          /network|fetch|failed to fetch|connection|aborted|timeout/i.test(
            String(error.message || ''),
          ));
      if (isNetworkError) {
        dispatch({
          type: VIBE_CODING_STOP_GENERATION,
          data: {
            reason: 'connection_lost',
            resumable: true,
          },
          meta: { requestId },
        });
        return {
          ok: true,
          stopped: true,
          data: {
            reason: 'connection_lost',
            resumable: true,
          },
        };
      }
      dispatch({
        type: VIBE_CODING_GENERATE_VIBE_RESPONSE_FAILURE,
        data: { error },
        meta: { requestId },
      });
      return { ok: false, error };
    } finally {
      if (activeStreamAbortController && activeStreamAbortController.signal === signal) {
        activeStreamAbortController = null;
      }
    }
  };
}

export function dismissGenerateVibeResponseError() {
  return { type: VIBE_CODING_GENERATE_VIBE_RESPONSE_DISMISS_ERROR };
}

export function reducer(state, action) {
  const meta = action.meta || {};
  switch (action.type) {
    case VIBE_CODING_STOP_GENERATION:
      if (!matchesActiveRequest(state, meta) && !meta.silent) return state;
      return {
        ...state,
        generateVibeResponsePending: false,
        isResumable: true,
        stoppedInfo: action.data || { reason: 'user_cancelled', resumable: true },
        milestones: updateMilestones(state.milestones, { type: 'stopped' }),
      };
    case VIBE_CODING_GENERATE_VIBE_RESPONSE_BEGIN: {
      const requestData = action.data || {};
      const isPlanApprove = requestData.planAction === 'approve';
      const prompt = typeof requestData.prompt === 'string' ? requestData.prompt.trim() : '';
      const attachments = Array.isArray(requestData.attachments) ? requestData.attachments : [];
      const nextMessages =
        prompt && !isPlanApprove
          ? [
              ...state.messages,
              {
                id: `temp-${Date.now()}`,
                role: 'user',
                content: prompt,
                attachments,
                createdAt: new Date().toISOString(),
              },
            ]
          : state.messages;
      const resumeCompleted = (state.stoppedInfo && state.stoppedInfo.completedTaskIds) || [];
      return {
        ...state,
        generateVibeResponsePending: true,
        generateVibeResponseError: null,
        isResumable: false,
        stoppedInfo: null,
        planProposal: isPlanApprove ? null : state.planProposal,
        activeStreamRequestId: meta.requestId || state.activeStreamRequestId,
        activeContextId: meta.contextId || state.activeContextId,
        ...RUN_SCOPED_RESET,
        runEvents: [],
        milestones:
          resumeCompleted.length > 0
            ? updateMilestones(createInitialMilestones(), {
                type: 'phase',
                name: 'resuming',
                completedTaskIds: resumeCompleted,
              })
            : createInitialMilestones(),
        lastEventSequence: 0,
        messages: nextMessages,
      };
    }
    case VIBE_CODING_GENERATE_VIBE_RESPONSE_EVENT: {
      if (!matchesActiveRequest(state, meta)) return state;
      const event = action.data;
      if (event.type === 'terminal_output') return state;
      if (event.sequence && event.sequence <= state.lastEventSequence) return state;
      const nextMilestones = updateMilestones(state.milestones, event);

      let nextStreaming = state.streamingMessage || '';
      let nextArtifact = state.activeArtifact ? { ...state.activeArtifact } : null;
      let nextTools = state.toolCalls ? [...state.toolCalls] : [];
      let nextBudget = state.budget;
      let nextPhase = state.currentPhase;
      let nextVerification = state.verificationResult;
      let nextRunMetadata = state.runMetadata;
      let nextConversationId = state.conversationId;
      let isPending = state.generateVibeResponsePending;
      let isResumable = state.isResumable;
      let stoppedInfo = state.stoppedInfo;
      let nextError = state.generateVibeResponseError;

      if (event.type === 'token') {
        nextStreaming += event.text || '';
      } else if (event.type === 'budget') {
        nextBudget = {
          elapsedMs: event.elapsedMs,
          remainingMs: event.remainingMs,
          modelTurns: event.modelTurns,
          maxModelTurns: event.maxModelTurns,
          toolCalls: event.toolCalls,
          maxToolCalls: event.maxToolCalls,
        };
      } else if (event.type === 'phase') {
        nextPhase = { name: event.name, detail: event.detail };
      } else if (event.type === 'run_started') {
        nextRunMetadata = {
          runId: event.runId,
          deadlineAt: event.deadlineAt,
          limits: event.limits,
          conversationId: event.conversationId,
          mode: event.mode,
        };
        if (event.conversationId) nextConversationId = event.conversationId;
      } else if (event.type === 'verification') {
        nextVerification = event.result;
      } else if (event.type === 'plan_ready') {
        isPending = false;
        const proposal = normalizePlanProposal({
          ...(event.proposal || event),
          conversationId: event.conversationId || (event.proposal || {}).conversationId,
        });
        return {
          ...state,
          generateVibeResponsePending: false,
          planPending: false,
          planProposal: proposal,
          milestones: nextMilestones,
          lastEventSequence: event.sequence || state.lastEventSequence,
          runEvents: [...state.runEvents, event],
        };
      } else if (event.type === 'stopped') {
        isPending = false;
        isResumable = Boolean(event.resumable);
        stoppedInfo = {
          reason: event.reason,
          resumable: event.resumable,
          changedFiles: event.changedFiles,
          currentTaskId: event.currentTaskId,
          completedTaskIds: event.completedTaskIds,
          planTaskIndex: event.planTaskIndex,
          taskCount: event.taskCount,
        };
        if (event.conversationId) nextConversationId = event.conversationId;
      } else if (event.type === 'failed') {
        isPending = false;
        isResumable = false;
        nextStreaming = '';
        const errorMessage =
          typeof event.error === 'string' ? event.error : 'Unable to generate a response.';
        const streamError = new Error(errorMessage);
        if (event.code) streamError.code = event.code;
        streamError.retryable = Boolean(event.retryable);
        nextError = streamError;
        if (event.conversationId) nextConversationId = event.conversationId;
      } else if (event.type === 'artifact_open') {
        nextArtifact = { id: event.id, title: event.title, files: [] };
      } else if (event.type === 'action_open') {
        if (!nextArtifact) {
          nextArtifact = { id: 'artifact', title: 'Code Changes', files: [] };
        }
        const exists = nextArtifact.files.find((f) => f.filePath === event.filePath);
        if (!exists) {
          nextArtifact.files = [
            ...nextArtifact.files,
            { filePath: event.filePath, status: 'writing' },
          ];
        }
      } else if (event.type === 'action_close') {
        if (nextArtifact) {
          nextArtifact.files = nextArtifact.files.map((f) =>
            f.filePath === event.filePath ? { ...f, status: 'saving' } : f,
          );
        }
      } else if (event.type === 'tool_started') {
        nextTools = [...nextTools, { tool: event.tool, args: event.args, status: 'running' }];
      } else if (event.type === 'file_saved') {
        if (nextArtifact) {
          nextArtifact.files = nextArtifact.files.map((f) =>
            f.filePath === event.path ? { ...f, status: 'saved' } : f,
          );
        }
      } else if (event.type === 'tool_completed') {
        nextTools = nextTools.map((t, idx) =>
          idx === nextTools.length - 1
            ? {
                ...t,
                status: event.success !== false ? 'completed' : 'failed',
                result: event.result,
              }
            : t,
        );
      }

      return {
        ...state,
        generateVibeResponsePending: isPending,
        generateVibeResponseError: nextError,
        isResumable,
        stoppedInfo,
        conversationId: nextConversationId,
        budget: nextBudget,
        currentPhase: nextPhase,
        verificationResult: nextVerification,
        runMetadata: nextRunMetadata,
        runEvents: [...state.runEvents, event],
        streamingMessage: nextStreaming,
        activeArtifact: nextArtifact,
        toolCalls: nextTools,
        milestones: nextMilestones,
        lastEventSequence: event.sequence || state.lastEventSequence,
      };
    }
    case VIBE_CODING_GENERATE_VIBE_RESPONSE_SUCCESS:
      if (!matchesActiveRequest(state, meta)) return state;
      return {
        ...state,
        generateVibeResponsePending: false,
        generateVibeResponseError: null,
        conversationId: action.data.conversationId || state.conversationId,
        runResult: action.data,
        planProposal: null,
        ...RUN_SCOPED_RESET,
        milestones: updateMilestones(state.milestones, { type: 'completed' }),
        messages: mergeUniqueMessages(
          state.messages,
          action.data.message
            ? [{ ...action.data.message, id: action.data.message.id || action.data.message.uuid }]
            : [],
        ),
      };
    case VIBE_CODING_GENERATE_VIBE_RESPONSE_FAILURE:
      if (!matchesActiveRequest(state, meta)) return state;
      return {
        ...state,
        generateVibeResponsePending: false,
        generateVibeResponseError: action.data.error,
        milestones: updateMilestones(state.milestones, { type: 'failed' }),
      };
    case VIBE_CODING_GENERATE_VIBE_RESPONSE_DISMISS_ERROR:
      return { ...state, generateVibeResponseError: null };
    default:
      return state;
  }
}
